import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'December 19, 2025';

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Header */}
      <section className="pt-20 pb-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-gray max-w-none">
            
            {/* Introduction */}
            <div className="mb-10">
              <p className="text-gray-700 leading-relaxed">
                At SuddyFlow, we take your privacy seriously. This Privacy Policy explains how Suddy Buckets LLC 
                ("Company," "we," "us," or "our") collects, uses, discloses, and protects your information when 
                you use our SuddyFlow platform and services (the "Service").
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using our Service, you agree to the collection and use of information in accordance with this 
                Privacy Policy. If you do not agree with our policies, please do not use our Service.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Information You Provide</h3>
              <p className="text-gray-700 leading-relaxed">
                When you use our Service, you may provide us with:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Account Information:</strong> Name, email address, password, phone number, and business information when you create an account</li>
                <li><strong>Business Data:</strong> Customer records, job details, invoices, expenses, and other business information you enter into the Service</li>
                <li><strong>Payment Information:</strong> Billing address and payment details (processed securely by our payment processor, Stripe)</li>
                <li><strong>Communications:</strong> Information you provide when contacting our support team</li>
                <li><strong>Uploaded Content:</strong> Photos, receipts, documents, and other files you upload to the Service</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Information Collected Automatically</h3>
              <p className="text-gray-700 leading-relaxed">
                When you access our Service, we automatically collect:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Device Information:</strong> Browser type, operating system, device type, and unique device identifiers</li>
                <li><strong>Usage Data:</strong> Pages viewed, features used, actions taken, and time spent on the Service</li>
                <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
                <li><strong>Location Data:</strong> General location based on IP address; precise location only if you enable GPS features</li>
                <li><strong>Cookies:</strong> Small data files stored on your device (see Section 6 below)</li>
              </ul>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Provide, maintain, and improve our Service</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Monitor and analyze usage trends to improve user experience</li>
                <li>Detect, investigate, and prevent fraudulent or unauthorized activity</li>
                <li>Comply with legal obligations and enforce our Terms of Service</li>
                <li>Personalize and improve the Service based on your preferences</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We do not sell your personal information to third parties. We do not use your business data 
                (customer records, invoices, etc.) for any purpose other than providing the Service to you.
              </p>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Share Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Service Providers:</strong> We share information with third-party vendors who assist us 
                in providing the Service (e.g., payment processing, email delivery, cloud hosting). These providers 
                are contractually obligated to protect your information.</li>
                <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of 
                assets, your information may be transferred as part of that transaction.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, 
                subpoena, or other legal process, or if we believe disclosure is necessary to protect our rights 
                or the safety of others.</li>
                <li><strong>With Your Consent:</strong> We may share your information with third parties when you 
                give us explicit consent to do so.</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service integrates with third-party services that have their own privacy policies. We encourage 
                you to review their policies:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Stripe:</strong> For payment processing. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Privacy Policy</a></li>
                <li><strong>Google Maps:</strong> For mapping and route optimization features. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Privacy Policy</a></li>
                <li><strong>Microsoft Azure:</strong> For cloud infrastructure and data storage. <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Microsoft Privacy Statement</a></li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Encryption of data in transit using SSL/TLS</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication requirements</li>
                <li>Secure cloud infrastructure with Microsoft Azure</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                While we strive to protect your information, no method of transmission over the Internet or 
                electronic storage is 100% secure. We cannot guarantee absolute security, but we are committed 
                to maintaining appropriate safeguards.
              </p>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Keep you signed in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our Service</li>
                <li>Improve our Service based on usage patterns</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling cookies may affect 
                the functionality of our Service. We do not use cookies for advertising purposes.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide you 
                with the Service. We may also retain and use your information as necessary to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements</li>
                <li>Support business operations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                If you delete your account, we will delete or anonymize your personal information within 30 days, 
                except as required for legal, accounting, or legitimate business purposes.
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Your Rights and Choices</h2>
              <p className="text-gray-700 leading-relaxed">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Export:</strong> Request an export of your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at privacy@suddyflow.com. We will respond to your 
                request within 30 days.
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service is not directed to individuals under the age of 18. We do not knowingly collect 
                personal information from children. If you believe we have collected information from a child, 
                please contact us immediately, and we will take steps to delete such information.
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. California Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                If you are a California resident, you have additional rights under the California Consumer Privacy 
                Act (CCPA), including the right to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Know what personal information we collect and how it is used</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of the sale of your personal information (we do not sell personal information)</li>
                <li>Non-discrimination for exercising your privacy rights</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise your CCPA rights, please contact us at privacy@suddyflow.com.
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service is hosted in the United States. If you access the Service from outside the United States, 
                please be aware that your information may be transferred to, stored, and processed in the United States. 
                By using the Service, you consent to such transfers.
              </p>
            </div>

            {/* Section 12 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you 
                to review this Privacy Policy periodically.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Your continued use of the Service after any changes constitutes your acceptance of the updated 
                Privacy Policy.
              </p>
            </div>

            {/* Section 13 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  <strong>Suddy Buckets LLC</strong><br />
                  Email: privacy@suddyflow.com<br />
                  Website: <a href="https://suddyflow.com" className="text-blue-600 hover:underline">suddyflow.com</a>
                </p>
              </div>
            </div>

            {/* Summary Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-10">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Privacy at a Glance</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>We never sell your personal information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Your business data is used only to provide the Service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>We use industry-standard encryption and security</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>You can request deletion of your data at any time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>We are transparent about how we use your information</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;